import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { FaBars, FaTimes, FaSun, FaMoon } from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const NavBarWrapper = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgb(var(--bg) / 0.55);
  backdrop-filter: blur(18px) saturate(1.2);
  -webkit-backdrop-filter: blur(18px) saturate(1.2);
  border-bottom: 1px solid rgb(var(--fg) / 0.05);
  transition: background 0.4s ease, border-color 0.4s ease;
`;

const NavBarInner = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  padding: 0 2rem;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: auto 1fr auto;
    padding: 0 1.2rem;
  }
`;

const NavLeft = styled.div`
  display: flex;
  align-items: center;
  justify-self: start;
`;

const NavCenter = styled.div`
  display: flex;
  align-items: center;
  gap: 2.4rem;
  justify-self: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  justify-self: end;
`;

const LogoLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: 0.85rem 0;
  transition: opacity 0.3s;
  &:hover { opacity: 0.85; }
`;

const LogoImg = styled.img`
  height: 28px;
  width: auto;
  display: block;

  @media (max-width: 768px) { height: 24px; }
`;

const navText = `
  text-decoration: none;
  color: rgb(var(--fg) / 0.45);
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  background: none;
  border: none;
  cursor: pointer;
  padding: 1.2rem 0;
  transition: color 0.3s;
  font-family: inherit;

  &:hover { color: rgb(var(--fg) / 0.9); }
`;

const NavLink = styled(Link)`${navText}`;

const DropdownWrapper = styled.div`
  position: relative;
`;

const DropdownLabel = styled.span`
  ${navText}
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
`;

const Chevron = styled.span`
  font-size: 0.45rem;
  opacity: 0.4;
  transition: transform 0.3s, opacity 0.3s;
`;

const DropdownPanel = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  min-width: 150px;
  padding-top: 0.3rem;
  z-index: 1001;

  & > div {
    padding: 0.4rem 0;
    background: rgb(var(--bg) / 0.92);
    backdrop-filter: blur(28px) saturate(1.3);
    -webkit-backdrop-filter: blur(28px) saturate(1.3);
    border: 1px solid rgb(var(--fg) / 0.08);
    border-radius: 12px;
    box-shadow: 0 24px 56px -8px rgb(0 0 0 / calc(0.55 * var(--shadow-mul)));
    animation: ${slideDown} 0.18s ease;
  }
`;

const dropItemStyle = `
  display: block;
  padding: 0.6rem 1.2rem;
  color: rgb(var(--fg) / 0.5);
  font-size: 0.78rem;
  font-weight: 500;
  letter-spacing: 0.05em;
  text-decoration: none;
  transition: color 0.25s, background 0.25s;
  white-space: nowrap;

  &:hover {
    color: rgb(var(--fg) / 0.92);
    background: rgb(var(--fg) / 0.05);
  }
`;

const ContentLink = styled(Link)`${dropItemStyle}`;
const ContentAnchor = styled.a`${dropItemStyle}`;

/* Theme toggle */

const ToggleBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 999px;
  border: 1px solid rgb(var(--fg) / 0.1);
  background: rgb(var(--fg) / 0.03);
  color: rgb(var(--fg) / 0.65);
  cursor: pointer;
  font-size: 0.95rem;
  transition: color 0.3s, border-color 0.3s, background 0.3s, transform 0.3s;
  font-family: inherit;
  padding: 0;

  &:hover {
    color: rgb(var(--fg) / 0.95);
    border-color: rgb(var(--fg) / 0.22);
    background: rgb(var(--fg) / 0.06);
    transform: translateY(-1px);
  }

  & svg { display: block; }

  @media (max-width: 768px) {
    width: 34px;
    height: 34px;
    font-size: 0.9rem;
  }
`;

/* Mobile */

const MobileToggle = styled.button`
  display: none;
  background: none;
  border: none;
  color: rgb(var(--fg) / 0.55);
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0.3rem;
  transition: color 0.3s;
  &:hover { color: rgb(var(--fg) / 0.95); }

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
  }
`;

const MobileOverlay = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: ${({ $open }) => ($open ? 'flex' : 'none')};
    position: fixed;
    inset: 0;
    z-index: 1001;
    background: rgb(var(--bg) / 0.97);
    backdrop-filter: blur(32px);
    -webkit-backdrop-filter: blur(32px);
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0;
  }
`;

const MobileCloseBtn = styled.button`
  position: absolute;
  top: 1.2rem;
  right: 1.2rem;
  background: none;
  border: none;
  color: rgb(var(--fg) / 0.45);
  font-size: 1.3rem;
  cursor: pointer;
  padding: 0.3rem;
  transition: color 0.3s;
  &:hover { color: rgb(var(--fg) / 0.95); }
`;

const MobileTopLink = styled(Link)`
  text-decoration: none;
  color: rgb(var(--fg) / 0.92);
  font-size: 1.2rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 0.7rem 0;
  transition: color 0.3s;
  &:hover { color: rgb(var(--fg)); }
`;

const MobileSubLink = styled(Link)`
  text-decoration: none;
  color: rgb(var(--gray) / 0.65);
  font-size: 0.85rem;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 0.35rem 0;
  transition: color 0.3s;
  &:hover { color: rgb(var(--fg) / 0.85); }
`;

const MobileSubAnchor = styled.a`
  text-decoration: none;
  color: rgb(var(--gray) / 0.65);
  font-size: 0.85rem;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 0.35rem 0;
  transition: color 0.3s;
  &:hover { color: rgb(var(--fg) / 0.85); }
`;

const MobileThemeRow = styled.div`
  margin-top: 1.4rem;
  display: flex;
  gap: 1rem;
`;

const MobileSpacer = styled.div`height: 1rem;`;

const HoverDropdown = ({ label, children }) => {
  const [open, setOpen] = useState(false);
  const timeout = useRef(null);
  const enter = () => { clearTimeout(timeout.current); setOpen(true); };
  const leave = () => { timeout.current = setTimeout(() => setOpen(false), 160); };
  useEffect(() => () => clearTimeout(timeout.current), []);

  return (
    <DropdownWrapper onMouseEnter={enter} onMouseLeave={leave}>
      <DropdownLabel style={{ color: open ? 'rgb(var(--fg) / 0.9)' : undefined }}>
        {label}
        <Chevron style={{ transform: open ? 'rotate(180deg)' : undefined, opacity: open ? 0.8 : undefined }}>▾</Chevron>
      </DropdownLabel>
      {open && <DropdownPanel><div>{children}</div></DropdownPanel>}
    </DropdownWrapper>
  );
};

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const closeMobile = () => setMobileOpen(false);

  // logo_dark.svg → light strokes (visible on dark theme)
  // logo_light.svg → dark strokes (visible on light theme)
  const logoSrc = theme === 'dark' ? '/logo_dark.svg' : '/logo_light.svg';

  return (
    <>
      <NavBarWrapper>
        <NavBarInner>
          <NavLeft>
            <LogoLink to="/" aria-label="Kyriakos Papadopoulos — Home">
              <LogoImg src={logoSrc} alt="KP" />
            </LogoLink>
          </NavLeft>

          <NavCenter>
            <NavLink to="/">Home</NavLink>
            <HoverDropdown label="Work">
              <ContentLink to="/projects">Projects</ContentLink>
              <ContentLink to="/blog">Blog</ContentLink>
            </HoverDropdown>
            <HoverDropdown label="About">
              <ContentLink to="/about">Bio</ContentLink>
              <ContentAnchor href="/files/cv.pdf" target="_blank" rel="noopener noreferrer">Resume</ContentAnchor>
              <ContentLink to="/contact">Contact</ContentLink>
            </HoverDropdown>
          </NavCenter>

          <NavRight>
            <ToggleBtn
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
              title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            >
              {theme === 'dark' ? <FaSun /> : <FaMoon />}
            </ToggleBtn>
            <MobileToggle onClick={() => setMobileOpen(true)} aria-label="Open menu">
              <FaBars />
            </MobileToggle>
          </NavRight>
        </NavBarInner>
      </NavBarWrapper>

      <MobileOverlay $open={mobileOpen}>
        <MobileCloseBtn onClick={closeMobile} aria-label="Close menu"><FaTimes /></MobileCloseBtn>
        <MobileTopLink to="/" onClick={closeMobile}>Home</MobileTopLink>
        <MobileSpacer />
        <MobileTopLink to="#" onClick={(e) => e.preventDefault()}>Work</MobileTopLink>
        <MobileSubLink to="/projects" onClick={closeMobile}>Projects</MobileSubLink>
        <MobileSubLink to="/blog" onClick={closeMobile}>Blog</MobileSubLink>
        <MobileSpacer />
        <MobileTopLink to="#" onClick={(e) => e.preventDefault()}>About</MobileTopLink>
        <MobileSubLink to="/about" onClick={closeMobile}>Bio</MobileSubLink>
        <MobileSubAnchor href="/files/cv.pdf" target="_blank" rel="noopener noreferrer" onClick={closeMobile}>Resume</MobileSubAnchor>
        <MobileSubLink to="/contact" onClick={closeMobile}>Contact</MobileSubLink>
        <MobileThemeRow>
          <ToggleBtn
            onClick={toggleTheme}
            aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          >
            {theme === 'dark' ? <FaSun /> : <FaMoon />}
          </ToggleBtn>
        </MobileThemeRow>
      </MobileOverlay>
    </>
  );
};

export default Navbar;
